import moment from 'moment-timezone';

import classNames from './styles.module.scss';

// ─── Arrow connector ──────────────────────────────────────────────────────────

const StepArrow = () => (
  <div className={classNames.stepConnector}>
    <svg width="14" height="14" fill="none" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M4 10h12M12 6l4 4-4 4"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

// ─── Step state derivation ────────────────────────────────────────────────────

const getStepStates = heroState => {
  if (heroState === 'approved') {
    return { s1: 'done', s2: 'done', s3: 'safe' };
  }
  if (heroState === 'submitted') {
    return { s1: 'done', s2: 'safe', s3: 'future' };
  }
  if (heroState === 'due-today' || heroState === 'overdue') {
    return { s1: 'active', s2: 'next', s3: 'future' };
  }
  // far-out or on-track
  return { s1: 'safe', s2: 'next', s3: 'future' };
};

// ─── Pill label helpers ───────────────────────────────────────────────────────

const getDaysUntil = (dateStr, parseFormats) => {
  if (!dateStr) return null;
  const d = moment(dateStr, parseFormats);
  if (!d.isValid()) return null;
  return moment(d.format('YYYY-MM-DD')).diff(moment().startOf('day'), 'days');
};

const getPillConfig = (t, stepState, daysUntil) => {
  if (stepState === 'done') {
    if (daysUntil !== null && daysUntil > 0) {
      return daysUntil <= 3
        ? {
            label: t('daysLeft', { count: daysUntil }),
            pillClass: 'pillWarning',
          }
        : { label: t('daysAway', { count: daysUntil }), pillClass: 'pillSafe' };
    }
    return { label: t('completedCheck'), pillClass: 'pillDone' };
  }
  if (stepState === 'active') {
    if (daysUntil !== null && daysUntil < 0)
      return {
        label: t('daysOverdueShort', { count: Math.abs(daysUntil) }),
        pillClass: 'pillToday',
      };
    return { label: t('dueToday'), pillClass: 'pillToday' };
  }
  if (daysUntil === null) return { label: '—', pillClass: 'pillFuture' };
  if (daysUntil < 0)
    return {
      label: t('daysOverdueShort', { count: Math.abs(daysUntil) }),
      pillClass: 'pillToday',
    };
  if (daysUntil === 0) return { label: t('dueToday'), pillClass: 'pillToday' };
  if (daysUntil <= 3)
    return {
      label: t('daysLeft', { count: daysUntil }),
      pillClass: 'pillWarning',
    };
  if (stepState === 'safe')
    return {
      label: t('daysAway', { count: daysUntil }),
      pillClass: 'pillSafe',
    };
  if (stepState === 'next')
    return {
      label: t('daysAway', { count: daysUntil }),
      pillClass: 'pillWarning',
    };
  return {
    label: t('daysAway', { count: daysUntil }),
    pillClass: 'pillFuture',
  };
};

// ─── Single step ──────────────────────────────────────────────────────────────

const STEP_CLASS_MAP = {
  safe: 'stepSafe',
  next: 'stepNext',
  future: 'stepFuture',
  active: 'stepActive',
  done: 'stepDone',
};

const Step = ({ t, num, name, dateLabel, stepState, daysUntil, clickable, onStepClick }) => {
  const stepClass = STEP_CLASS_MAP[stepState] || 'stepFuture';
  const { label: pillLabel, pillClass } = getPillConfig(t, stepState, daysUntil);

  return (
    <div
      className={[
        classNames.step,
        classNames[stepClass],
        clickable ? classNames.stepClickable : '',
      ].join(' ')}
      onClick={clickable ? onStepClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className={classNames.stepNumRow}>
        <div className={classNames.stepNum}>{num}</div>
        <div className={classNames.stepName}>{name}</div>
      </div>
      {dateLabel && <div className={classNames.stepDate}>{dateLabel}</div>}
      <span className={[classNames.stepPill, classNames[pillClass]].join(' ')}>{pillLabel}</span>
      {clickable && <span className={classNames.stepActionHint}>{t('submitMyTrips')} →</span>}
    </div>
  );
};

// ─── WhatHappensNext ──────────────────────────────────────────────────────────

const DATE_FORMATS = ['MM/DD/YYYY H:mm:ss', 'MM/DD/YYYY HH:mm:ss', moment.ISO_8601];

const WhatHappensNext = ({ t, currentPeriod, heroState, onSubmitTrips }) => {
  const { s1, s2, s3 } = getStepStates(heroState);

  const submissionDate = currentPeriod?.dateOfTripSubmissionDeadline
    ? moment(currentPeriod.dateOfTripSubmissionDeadline, DATE_FORMATS).format('MMM D, YYYY')
    : null;

  const approvalDate = currentPeriod?.dateOfManagerApprovalDeadline
    ? moment(currentPeriod.dateOfManagerApprovalDeadline, DATE_FORMATS).format('MMM D, YYYY')
    : null;

  const paymentDate =
    currentPeriod?.achPaymentDate || currentPeriod?.paymentDate
      ? moment(currentPeriod.achPaymentDate || currentPeriod.paymentDate, DATE_FORMATS).format(
          'MMM D, YYYY',
        )
      : null;

  const daysUntilSubmission = getDaysUntil(
    currentPeriod?.dateOfTripSubmissionDeadline,
    DATE_FORMATS,
  );
  const daysUntilApproval = getDaysUntil(
    currentPeriod?.dateOfManagerApprovalDeadline,
    DATE_FORMATS,
  );
  const daysUntilPayment = getDaysUntil(
    currentPeriod?.achPaymentDate || currentPeriod?.paymentDate,
    DATE_FORMATS,
  );

  return (
    <>
      <p className={classNames.upcomingLabel}>{t('whatHappensNext')}</p>
      <div className={classNames.stepsRow}>
        <Step
          t={t}
          num={1}
          name={t('tripSubmissions')}
          dateLabel={submissionDate ? t('byDate', { date: submissionDate }) : null}
          stepState={s1}
          daysUntil={daysUntilSubmission}
          clickable={s1 === 'safe' || s1 === 'active'}
          onStepClick={onSubmitTrips}
        />
        <StepArrow />
        <Step
          t={t}
          num={2}
          name={t('managerReviews')}
          dateLabel={approvalDate ? t('byDate', { date: approvalDate }) : null}
          stepState={s2}
          daysUntil={daysUntilApproval}
          clickable={false}
        />
        <StepArrow />
        <Step
          t={t}
          num={3}
          name={t('reimbursementInitiated')}
          dateLabel={paymentDate || null}
          stepState={s3}
          daysUntil={daysUntilPayment}
          clickable={false}
        />
      </div>
    </>
  );
};

export default WhatHappensNext;
